import type { SoundCue } from '../domain/types';

interface Tone {
  readonly freq: number;
  readonly duration: number;
  readonly wave?: OscillatorType;
  /** Seconds after the cue is played. */
  readonly delay?: number;
}

const CUES: Readonly<Record<SoundCue, readonly Tone[]>> = {
  tap: [{ freq: 720, duration: 0.06 }],
  clear: [{ freq: 440, duration: 0.05 }],
  back: [{ freq: 520, duration: 0.06 }],
  advance: [{ freq: 880, duration: 0.1 }],
  start: [{ freq: 1046, duration: 0.18 }],
  cancel: [{ freq: 440, duration: 0.1 }],
  countdownTick: [{ freq: 660, duration: 0.06 }],
  countdownFinal: [{ freq: 990, duration: 0.06 }],
  emissionStart: [{ freq: 1320, duration: 0.5 }],
  granted: [
    { freq: 988, duration: 0.1, delay: 0.1 },
    { freq: 1319, duration: 0.16, delay: 0.22 },
  ],
  error: [{ freq: 130, duration: 0.35, wave: 'sawtooth' }],
  complete: [
    { freq: 660, duration: 0.15 },
    { freq: 880, duration: 0.15, delay: 0.15 },
    { freq: 1320, duration: 0.35, delay: 0.3 },
  ],
  alarm: [{ freq: 300, duration: 0.15, wave: 'sawtooth' }],
  resume: [{ freq: 660, duration: 0.08 }],
  toggleOn: [{ freq: 520, duration: 0.06 }],
  toggleOff: [{ freq: 720, duration: 0.06 }],
};

const PEAK_GAIN = 0.05;

export interface AudioFeedback {
  play(cue: SoundCue): void;
}

/** Short synthesized beeps — no audio assets to ship or cache. */
export class WebAudioFeedback implements AudioFeedback {
  #context: AudioContext | null = null;
  readonly #enabled: boolean;

  constructor(enabled: boolean) {
    this.#enabled = enabled;
  }

  play(cue: SoundCue): void {
    const ctx = this.#getContext();
    if (!ctx) return;
    // Browsers keep the context suspended until the first user gesture; cues before that are dropped.
    if (ctx.state === 'suspended') void ctx.resume().catch(() => undefined);
    for (const tone of CUES[cue]) this.#schedule(ctx, tone);
  }

  #getContext(): AudioContext | null {
    if (!this.#enabled) return null;
    if (!this.#context && typeof AudioContext !== 'undefined') {
      try {
        this.#context = new AudioContext();
      } catch {
        return null;
      }
    }
    return this.#context;
  }

  #schedule(ctx: AudioContext, { freq, duration, wave = 'square', delay = 0 }: Tone): void {
    const start = ctx.currentTime + delay;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = wave;
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(PEAK_GAIN, start);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    osc.connect(gain).connect(ctx.destination);
    osc.start(start);
    osc.stop(start + duration);
  }
}

export const silentAudio: AudioFeedback = { play: () => undefined };
